import { useCallback } from "react";
import { format, parseISO } from "date-fns";
import { Button, Tag } from "@carbon/react";
import { Download } from "@carbon/icons-react";
import {
  ActivityLog,
  ActivityLogAction,
  ActivityLogEntityType,
} from "../../types";
import i18next from "../../i18n";
import "./activityTimeline.scss";

interface ActivityTimelineProps {
  logs: ActivityLog[];
  purchaseRequestId: string;
}

const getActionLabel = (action: ActivityLogAction): string =>
  i18next.t(`activityLog.actions.${action}`, { defaultValue: action });

const getEntityLabel = (entityType: ActivityLogEntityType): string =>
  i18next.t(`activityLog.entity.${entityType}`, { defaultValue: entityType });

// Entity → Carbon Tag colour (the left rail mirrors it via `activity-log__item--<type>`).
const ENTITY_TAG_TYPE: Record<
  ActivityLogEntityType,
  "blue" | "purple" | "teal" | "green"
> = {
  [ActivityLogEntityType.PURCHASE_REQUEST]: "blue",
  [ActivityLogEntityType.PURCHASE_REQUEST_ITEM]: "purple",
  [ActivityLogEntityType.ORDER]: "teal",
  [ActivityLogEntityType.OFFER]: "green",
};

const formatLogDate = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), "dd/MM/yyyy HH:mm");
  } catch {
    return dateStr;
  }
};

const buildCsvContent = (logs: ActivityLog[]): string => {
  const headers = [
    i18next.t("activityLog.csv.date"),
    i18next.t("activityLog.csv.action"),
    i18next.t("activityLog.csv.entityType"),
    i18next.t("activityLog.csv.material"),
    i18next.t("activityLog.csv.previousState"),
    i18next.t("activityLog.csv.newState"),
    i18next.t("activityLog.csv.performedBy"),
    i18next.t("activityLog.csv.description"),
  ];

  const escape = (value: string | null | undefined) => {
    if (value == null) return "";
    const str = String(value).replace(/"/g, '""');
    return `"${str}"`;
  };

  const rows = logs.map((log) => [
    escape(formatLogDate(log.createdAt)),
    escape(getActionLabel(log.action) || log.action),
    escape(getEntityLabel(log.entityType)),
    escape(log.materialName),
    escape(log.previousState),
    escape(log.newState),
    escape(log.performedBy?.fullName),
    escape(log.description),
  ]);

  return [
    headers.map((h) => `"${h}"`).join(","),
    ...rows.map((r) => r.join(",")),
  ].join("\n");
};

const ActivityTimeline = ({
  logs,
  purchaseRequestId,
}: ActivityTimelineProps) => {
  const handleDownload = useCallback(() => {
    const csv = buildCsvContent(logs);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `activity-log-${purchaseRequestId.slice(0, 8)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [logs, purchaseRequestId]);

  if (!logs.length) {
    return (
      <p className="activity-log__empty">{i18next.t("activityLog.noActivity")}</p>
    );
  }

  return (
    <div className="activity-log">
      <div className="activity-log__toolbar">
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Download}
          onClick={handleDownload}
        >
          {i18next.t("activityLog.downloadCsv")}
        </Button>
      </div>

      <ol className="activity-log__list">
        {logs.map((log) => (
          <li
            key={log.id}
            className={`activity-log__item activity-log__item--${log.entityType}`}
          >
            <div className="activity-log__head">
              <Tag type={ENTITY_TAG_TYPE[log.entityType] || "gray"} size="sm">
                {getEntityLabel(log.entityType)}
              </Tag>
              <span className="activity-log__action">
                {getActionLabel(log.action) || log.action}
              </span>
              <time className="activity-log__time">
                {formatLogDate(log.createdAt)}
              </time>
            </div>

            {log.description && (
              <p className="activity-log__desc">{log.description}</p>
            )}

            <div className="activity-log__meta">
              {log.materialName && (
                <span>
                  <span className="activity-log__meta-key">
                    {i18next.t("activityLog.material")}:
                  </span>{" "}
                  {log.materialName}
                </span>
              )}
              {(log.previousState || log.newState) && (
                <span>
                  <span className="activity-log__meta-key">
                    {i18next.t("activityLog.state")}:
                  </span>{" "}
                  {log.previousState && (
                    <span className="activity-log__state-prev">
                      {log.previousState}
                    </span>
                  )}
                  {log.previousState && log.newState && " → "}
                  {log.newState && (
                    <span className="activity-log__state-new">
                      {log.newState}
                    </span>
                  )}
                </span>
              )}
              {log.performedBy && (
                <span>
                  <span className="activity-log__meta-key">
                    {i18next.t("activityLog.by")}:
                  </span>{" "}
                  {log.performedBy.fullName}
                </span>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default ActivityTimeline;
